import React, { useMemo, useState } from 'react';
import { getPartProgress, clearPartProgress } from './_progress';
import { ConfirmModal } from './_layout';
import { T, PART_META } from './_theme';
import { ReadingFrame, Pill, PrimaryBtn, GhostBtn } from './_primitives';
import { useAuth } from '../../context/AuthContext';

// Reading home — gallery-style. Warm beige page, score card with circular
// ring + per-part progress bars, "next up" focus banner, stats strip
// (Mistakes / Vocab / Exam mode), and the 5-parts list.

// Authoritative titles for the 5-part FET reading layout. Used as a fallback
// in case backend data still has the legacy 6-part labels (pre-migration).
const PART_TITLES = {
  1: 'Signs & Notices',
  2: 'Gap Fill',
  3: 'Text Matching',
  4: 'People ↔ Place',
  5: 'Long Reading',
};

// Compute question count from the part's content using the NEW 5-part
// shape. Used as a fallback when the backend is still running its old
// `question_count` property (which looks at field names like `people` /
// `statements` / `blanks` and returns 0 for the new shape's `gaps` /
// `questions` / `items`).
function questionsForPart(part) {
  const c = (part && part.content) || {};
  switch (part?.part_number) {
    case 1: return Array.isArray(c.signs) ? c.signs.length : 0;
    case 2: return Array.isArray(c.gaps) ? c.gaps.length : 0;
    case 3: return Array.isArray(c.questions) ? c.questions.length : 0;
    case 4: return Array.isArray(c.items) ? c.items.length : 0;
    case 5: return Array.isArray(c.questions) ? c.questions.length : 0;
    default: return 0;
  }
}
function partQuestionCount(part) {
  // Trust the backend's number ONLY if it's > 0 — when the old backend
  // returns 0 because of stale field names but the new content is present,
  // fall back to the client-side count.
  const backend = Number(part?.question_count || 0);
  if (backend > 0) return backend;
  return questionsForPart(part);
}

export default function ReadingHome({
  isMobile,
  navigate,
  exam,
  loading,
  setToast,
  onOpenPart,
  onOpenExamMode,
  onOpenMistakes,
  onOpenVocabulary,
  onOpenTraining,
}) {
  // Defensive: clamp to parts 1–5. The new schema only has 5 parts; if the
  // backend still has legacy Part 6 data (pre-migration), drop it on the
  // client so the UI doesn't show "Open Cloze" alongside the new layout.
  const readingParts = useMemo(() => (
    (exam?.reading_parts || [])
      .filter((p) => Number(p.part_number) >= 1 && Number(p.part_number) <= 5)
      .slice()
      .sort((a, b) => (a.part_number || 0) - (b.part_number || 0))
  ), [exam]);

  const totalQuestions = readingParts.reduce((acc, p) => acc + partQuestionCount(p), 0);
  const totalMarks = totalQuestions; // 1 question = 1 mark in the FET reading exam
  const readyParts = readingParts.filter((p) => p.has_content && partQuestionCount(p) > 0);

  const [bump, setBump] = useState(0);
  const [confirm, setConfirm] = useState(null);
  // Pre-flight "Start your timed exam?" gate — fires whenever the student
  // taps the Exam pill or the "Try the full exam" button. Practice flow has
  // no popup; only the timed attempt does.
  const [showStartExam, setShowStartExam] = useState(false);
  const { user } = useAuth();
  // Plan-based access check. We prefer the backend's authoritative
  // capability_map (`user.capabilities.reading`) when present, but fall
  // back to checking `user.plan` directly so the gate doesn't silently
  // fail open for users on a backend version that hasn't yet added the
  // capabilities field. Free plan can practise reading but can't take
  // the timed reading exam.
  const readingLocked = (() => {
    if (!user) return true; // anonymous — block until they log in
    if (user.capabilities && typeof user.capabilities.reading === 'boolean') {
      return user.capabilities.reading === false;
    }
    // Plan-based fallback. Mirror backend's can_access_reading() so the
    // lock is consistent regardless of which signal is available. Prefer
    // `effective_plan` so an expired promo trial flips to free here too.
    const allowedPlans = new Set(['promo', 'basic', 'ai']);
    const planKey = String(user.effective_plan || user.plan || 'free').toLowerCase();
    return !allowedPlans.has(planKey);
  })();
  const partProgress = useMemo(() => getPartProgress(), [bump]);
  const completedCount = readyParts.filter((p) => partProgress[String(p.part_number)]).length;

  // Aggregate score across parts attempted so far.
  const scored = readingParts
    .map((p) => partProgress[String(p.part_number)])
    .filter((s) => s && Number(s.max) > 0);
  const totalScore = scored.reduce((acc, s) => acc + Number(s.score || 0), 0);
  const totalScoreMax = scored.reduce((acc, s) => acc + Number(s.max || 0), 0);
  const totalScorePct = totalScoreMax > 0 ? totalScore / totalScoreMax : 0;
  const overallPercentLabel = totalScoreMax > 0 ? `${Math.round(totalScorePct * 100)}%` : '—';

  // Pick the weakest scored part as the "Focus on" suggestion.
  const weakest = useMemo(() => {
    let best = null;
    readingParts.forEach((p) => {
      const s = partProgress[String(p.part_number)];
      if (!s || !Number(s.max)) return;
      const ratio = Number(s.score || 0) / Number(s.max || 1);
      if (best === null || ratio < best.ratio) best = { part: p, ratio };
    });
    return best;
  }, [readingParts, partProgress]);

  const totalMistakes = useMemo(() => (
    Object.values(partProgress).reduce((acc, p) => acc + (Array.isArray(p?.mistakes) ? p.mistakes.length : 0), 0)
  ), [partProgress]);

  const handleResetProgress = () => {
    setConfirm({
      title: 'Clear practice progress?',
      message: `This wipes your scores and mistakes from ${completedCount} part${completedCount === 1 ? '' : 's'}. The reading exam content stays.`,
      confirmLabel: 'Clear progress',
      tone: 'danger',
      icon: '⚠',
      onConfirm: () => {
        clearPartProgress();
        setBump((n) => n + 1);
        setToast('Practice progress cleared.');
      },
    });
  };

  const handlePartClick = (part) => {
    const ready = !!part.has_content && partQuestionCount(part) > 0;
    if (!ready) {
      setToast("This reading part doesn't have content yet. An admin can add it from the admin panel.");
      return;
    }
    if (onOpenPart) {
      onOpenPart(part);
      return;
    }
    setToast(`Part ${part.part_number} — coming in the next update.`);
  };

  if (loading) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div>
      </ReadingFrame>
    );
  }

  // Plan gate at the section root — free-plan students should never see
  // the reading practice / exam screens at all. The same lock fires when
  // they bookmark /?mode=reading-home or any per-part URL.
  if (readingLocked) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <div style={{ padding: 24, display: 'grid', placeItems: 'center', minHeight: '70vh' }}>
          <div style={{
            background: T.surface, borderRadius: 18,
            border: `1px solid ${T.hairline}`,
            maxWidth: 460, width: '100%', padding: 24,
            fontFamily: T.sans,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{
                width: 42, height: 42, borderRadius: 12,
                background: T.blueBg, color: T.blue,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }} aria-hidden="true">🔒</span>
              <div>
                <div style={{ fontSize: 11, color: T.ink3, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  FET · Reading
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, lineHeight: 1.25, marginTop: 2 }}>
                  Plan upgrade required
                </div>
              </div>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6, marginBottom: 18 }}>
              Reading isn't part of your current plan. Upgrade once and you'll unlock every reading exam — practice and timed — on the platform.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <GhostBtn onClick={() => navigate('/dashboard')}>Back to dashboard</GhostBtn>
              <PrimaryBtn full={false} onClick={() => navigate('/pricing?locked=reading&return_to=/dashboard')}>
                🔒 View plans
              </PrimaryBtn>
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  return (
    <ReadingFrame isMobile={isMobile}>
      {/* Top bar */}
      <div style={{ padding: '18px 16px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: T.ink3, fontWeight: 600, letterSpacing: 0.3, fontFamily: T.sans, textTransform: 'uppercase' }}>FET · Reading</div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.6, marginTop: 2, color: T.ink, fontFamily: T.sans }}>
            {exam?.title || 'Practice'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={{
            height: 36, borderRadius: 999,
            border: `1px solid ${T.hairline}`, background: T.surface,
            color: T.ink2, padding: '0 14px',
            fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.sans,
          }}
        >← Dashboard</button>
      </div>

      {/* Mode pills */}
      <div style={{ padding: '6px 16px 0' }}>
        <div style={{
          display: 'inline-flex', background: T.bgSunk, borderRadius: 9999,
          padding: 3, border: `1px solid ${T.hairline}`,
        }}>
          <span style={{
            padding: '6px 14px', borderRadius: 9999,
            fontSize: 12, fontWeight: 600,
            background: T.surface, color: T.ink,
            boxShadow: '0 1px 2px rgba(70,55,30,.04)',
            fontFamily: T.sans,
          }}>🌿 Practice</span>
          <button
            type="button"
            onClick={() => setShowStartExam(true)}
            style={{
              padding: '6px 14px', borderRadius: 9999,
              fontSize: 12, fontWeight: 600,
              background: 'transparent', color: T.ink3,
              border: 'none', cursor: 'pointer', fontFamily: T.sans,
            }}
          >⏱ Exam</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 16px 24px', overflow: 'auto', flex: 1 }}>
        {readyParts.length === 0 ? (
          <div style={{
            background: T.surface, borderRadius: 20, border: `1px solid ${T.hairline}`,
            padding: 22, color: T.ink2, lineHeight: 1.7, fontFamily: T.sans,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: T.ink }}>
              No reading parts uploaded yet
            </div>
            <div style={{ fontSize: 14 }}>
              An admin needs to add content to at least one of the 5 reading parts before this exam is ready to attempt.
            </div>
          </div>
        ) : (
          <>
            {/* Score card */}
            <div style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.hairline}`, padding: '18px 18px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{
                  fontSize: 12, color: T.ink3, fontWeight: 600,
                  letterSpacing: 0.3, textTransform: 'uppercase', fontFamily: T.sans,
                }}>Your score</span>
                <Pill tone="blue">5 parts · {totalQuestions} marks</Pill>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 44, fontWeight: 700, letterSpacing: -1.5, lineHeight: 1, color: T.ink, fontFamily: T.sans,
                }}>{totalScoreMax > 0 ? totalScore : 0}</span>
                <span style={{ fontSize: 18, color: T.ink3, fontWeight: 500, fontFamily: T.sans }}>
                  / {totalScoreMax || totalMarks}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: T.ok, fontWeight: 600, fontFamily: T.sans }}>
                  {overallPercentLabel}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginTop: 14 }}>
                {PART_META.map((meta) => {
                  const part = readingParts.find((p) => p.part_number === meta.n);
                  const partTotal = partQuestionCount(part);
                  const score = partProgress[String(meta.n)];
                  const partScore = score?.score ?? 0;
                  const partMax = score?.max ?? partTotal;
                  const pct = partMax > 0 ? (partScore / partMax) * 100 : 0;
                  return (
                    <div key={meta.n}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: T.ink2, fontFamily: T.sans }}>P{meta.n}</span>
                        <span style={{ fontFamily: T.mono, color: T.ink3 }}>{partTotal ? `${partScore}/${partMax}` : '—'}</span>
                      </div>
                      <div style={{ height: 6, background: T.bgSunk, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next-up focus banner */}
            {weakest && (
              <div style={{
                marginTop: 12, width: '100%',
                display: 'flex', alignItems: 'center', gap: 12,
                background: T.hintBg, border: `1px solid ${T.hintEdge}`,
                borderRadius: 14, padding: '12px 14px', fontFamily: T.sans,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: T.hint, color: '#fff',
                  display: 'grid', placeItems: 'center',
                  fontSize: 16, fontWeight: 700,
                }}>↗</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: T.hint, fontWeight: 700 }}>Next up</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 1, color: T.ink }}>
                    Focus on Part {weakest.part.part_number} — {PART_TITLES[weakest.part.part_number]}
                  </div>
                </div>
              </div>
            )}

            {/* Quick stats strip */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12 }}>
              {[
                { l: 'Mistakes', v: String(totalMistakes), i: '✕', c: T.miss, action: onOpenMistakes },
                { l: 'Vocab', v: '—', i: '✎', c: T.blue, action: onOpenVocabulary },
                { l: 'Exam mode', v: '45m', i: '⏱', c: T.ink2, action: () => setShowStartExam(true) },
              ].map((q) => (
                <button
                  key={q.l}
                  type="button"
                  onClick={q.action}
                  style={{
                    padding: '10px 8px',
                    background: T.surface, borderRadius: 14,
                    border: `1px solid ${T.hairline}`,
                    cursor: 'pointer', fontFamily: T.sans, textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: q.c, fontSize: 14, fontWeight: 700 }}>{q.i}</span>
                    <span style={{ fontSize: 11, color: T.ink3, fontWeight: 600, letterSpacing: 0.2, textTransform: 'uppercase' }}>{q.l}</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.4, color: T.ink }}>{q.v}</div>
                </button>
              ))}
            </div>

            {/* All 5 parts list */}
            <div style={{ marginTop: 20, marginBottom: 8, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontFamily: T.sans }}>All 5 parts</div>
              <span style={{ fontSize: 11, color: T.ink3, fontFamily: T.sans }}>Tap to practise</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PART_META.map((meta) => {
                const part = readingParts.find((p) => p.part_number === meta.n);
                const partTotal = partQuestionCount(part);
                const isReady = part && part.has_content && partTotal > 0;
                const score = partProgress[String(meta.n)];
                const partScore = score?.score;
                const partMax = score?.max ?? partTotal;
                const isDone = score && partTotal > 0 && partScore === partMax;
                let tail = 'Coming soon';
                let tailColor = T.ink3;
                if (isReady) {
                  if (isDone) { tail = 'Done ✓'; tailColor = T.ok; }
                  else if (score) { tail = 'Resume →'; tailColor = T.blue; }
                  else { tail = 'Start →'; tailColor = T.blue; }
                }
                return (
                  <button
                    key={meta.n}
                    type="button"
                    onClick={() => part && handlePartClick(part)}
                    disabled={!isReady}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      background: T.surface,
                      border: `1px solid ${T.hairline}`,
                      borderRadius: 14,
                      cursor: isReady ? 'pointer' : 'default',
                      opacity: isReady ? 1 : 0.6,
                      width: '100%', textAlign: 'left',
                      fontFamily: T.sans,
                    }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 10,
                      background: meta.color, color: '#fff',
                      display: 'grid', placeItems: 'center',
                      fontSize: 14, fontWeight: 700,
                    }}>{meta.n}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{meta.title}</div>
                      <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>
                        {partTotal} qs · {partTotal} marks
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 12, fontFamily: T.mono,
                        color: isDone ? T.ok : T.ink2, fontWeight: 600,
                      }}>{score ? `${partScore}/${partMax}` : (isReady ? '—' : '0/0')}</div>
                      <div style={{ fontSize: 11, color: tailColor, fontWeight: 600, marginTop: 2 }}>{tail}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer actions */}
            <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <PrimaryBtn full={false} onClick={() => setShowStartExam(true)}>Try the full exam (45 min) →</PrimaryBtn>
              <div style={{ flex: 1 }} />
              {completedCount > 0 && (
                <GhostBtn onClick={handleResetProgress}>Clear progress</GhostBtn>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmModal state={confirm} onClose={() => setConfirm(null)} theme={{
        // ConfirmModal expects a theme object — supply minimal tokens.
        surface: T.surface,
        surfaceSoft: T.bgSunk,
        border: T.hairline,
        text: T.ink,
        body: T.ink2,
        muted: T.ink3,
        accent: T.blue,
        accentSoftBg: T.blueBg,
        ctaBg: T.blue,
        ctaText: '#fff',
        shadow: '0 1px 3px rgba(70,55,30,.06), 0 4px 14px rgba(70,55,30,.05)',
      }} />

      {showStartExam && (
        <StartExamModal
          totalQuestions={readingParts.reduce((sum, p) => sum + partQuestionCount(p), 0)}
          totalParts={readingParts.length}
          planLocked={readingLocked}
          onCancel={() => setShowStartExam(false)}
          onConfirm={() => { setShowStartExam(false); if (onOpenExamMode) onOpenExamMode(); }}
          onUpgradePlan={() => { setShowStartExam(false); navigate('/pricing?locked=reading&return_to=/dashboard'); }}
        />
      )}
    </ReadingFrame>
  );
}

function StartExamModal({ totalQuestions, totalParts, planLocked, onCancel, onConfirm, onUpgradePlan }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'grid', placeItems: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface, borderRadius: 18,
          maxWidth: 460, width: '100%', padding: 24,
          boxShadow: '0 18px 48px rgba(15,23,42,.28)',
          fontFamily: T.sans,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{
            width: 42, height: 42, borderRadius: 12,
            background: T.blueBg, color: T.blue,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }} aria-hidden="true">{planLocked ? '🔒' : '⏱'}</span>
          <div>
            <div style={{ fontSize: 11, color: T.ink3, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Reading · Exam mode
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, lineHeight: 1.25, marginTop: 2 }}>
              {planLocked ? 'Plan upgrade required' : 'Start your timed exam?'}
            </div>
          </div>
        </div>
        {planLocked ? (
          <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6, marginBottom: 18 }}>
            Reading exams aren't part of your current plan. Upgrade once and you'll unlock every reading exam on the platform.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6, marginBottom: 8 }}>
              You're about to begin the full FET reading exam.
            </div>
            <ul style={{ margin: '0 0 18px', paddingInlineStart: 18, color: T.ink2, fontSize: 13, lineHeight: 1.7 }}>
              <li><strong style={{ color: T.ink }}>45 minutes</strong> on the clock — once you start, the timer keeps running even if you leave the page.</li>
              <li><strong style={{ color: T.ink }}>{totalQuestions} questions</strong> across {totalParts} parts.</li>
              <li>No feedback or hints — like the real test.</li>
              <li>You can leave and come back; your answers and remaining time are saved.</li>
            </ul>
          </>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <GhostBtn onClick={onCancel}>Cancel</GhostBtn>
          {planLocked ? (
            <PrimaryBtn full={false} onClick={onUpgradePlan}>🔒 View plans</PrimaryBtn>
          ) : (
            <PrimaryBtn full={false} onClick={onConfirm}>Start exam ✓</PrimaryBtn>
          )}
        </div>
      </div>
    </div>
  );
}
